import { FC, useEffect, useRef, useState } from 'react'
import { styled } from '@mui/material/styles'
import Button, { ButtonProps } from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Grow from '@mui/material/Grow'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import { useSelector } from 'react-redux'
import { RootState } from 'dataStore/state'
import { useActions } from 'hooks/useActions'

interface CustomersPizzeriaFilterProps {
  /** Выбранный id пиццерии или null */
  selectedPizzeriaId: number | null
  /**
   * Колбэк смены пиццерии (локальный filter, не Redux orders).
   * @param pizzeriaId — id пиццерии или null
   */
  onChange: (pizzeriaId: number | null) => void
}

const ColorButton = styled(Button)<ButtonProps>(() => ({
  color: '#6c757d',
  backgroundColor: '#fff',
  '&:hover': {
    backgroundColor: '#ffffff',
  },
}))

/**
 * Фильтр пиццерии для страницы клиентов без записи в orders.filter.
 * @param selectedPizzeriaId — текущий выбор
 * @param onChange — колбэк
 */
const CustomersPizzeriaFilter: FC<CustomersPizzeriaFilterProps> = ({
  selectedPizzeriaId,
  onChange,
}) => {
  const { pizzerias } = useSelector((state: RootState) => state.pizzerias)
  const { getAllPizzerias } = useActions()
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)
  const [options, setOptions] = useState([{ id: 0, value: 'Все пиццерии' }])

  useEffect(() => {
    if (!pizzerias?.length) {
      getAllPizzerias()
    }
  }, [])

  useEffect(() => {
    setOptions([
      { id: 0, value: 'Все пиццерии' },
      ...(pizzerias?.map(pizzeriaItem => ({
        id: pizzeriaItem.id!,
        value: `${pizzeriaItem?.address?.city}, ${pizzeriaItem?.address?.street}`,
      })) ?? []),
    ])
  }, [pizzerias])

  const selectedOption =
    options.find(
      option => option.id === (selectedPizzeriaId ?? 0)
    )?.value ?? 'Все пиццерии'

  /**
   * Выбирает пиццерию из меню.
   * @param pizzeriaId — id опции (0 = все)
   */
  const handleMenuItemClick = (pizzeriaId: number): void => {
    setOpen(false)
    onChange(pizzeriaId === 0 ? null : pizzeriaId)
  }

  const handleToggle = (): void => {
    setOpen(previousOpen => !previousOpen)
  }

  /**
   * Закрывает меню при клике снаружи.
   * @param event — DOM-событие
   */
  const handleClose = (event: Event): void => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return
    }
    setOpen(false)
  }

  return (
    <>
      <ButtonGroup
        variant='contained'
        ref={anchorRef}
        aria-label='customers pizzeria filter'
        sx={{ borderRadius: 28, width: '100%', boxShadow: 3 }}
      >
        <ColorButton
          onClick={handleToggle}
          sx={{ borderRadius: 28, overflow: 'hidden', whiteSpace: 'nowrap' }}
          fullWidth
        >
          {selectedOption}
        </ColorButton>
        <ColorButton
          size='small'
          aria-controls={open ? 'customers-pizzeria-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup='menu'
          onClick={handleToggle}
          sx={{ borderRadius: 28 }}
        >
          <ArrowDropDownIcon />
        </ColorButton>
      </ButtonGroup>
      <Popper
        sx={{ zIndex: 1, borderRadius: 28 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id='customers-pizzeria-menu' autoFocusItem>
                  {options.map(option => (
                    <MenuItem
                      key={option.id}
                      selected={option.id === (selectedPizzeriaId ?? 0)}
                      onClick={() => handleMenuItemClick(option.id)}
                    >
                      {option.value}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}

export default CustomersPizzeriaFilter
